import {createSelector} from 'reselect';
import {Iterable} from 'immutable';

import {getReviews, getTranslations, getAbacusTests, getTravelerFeatures} from './store';
import {getOwnerDetails} from './ownerDetails';
import {getBearStayReviewData, getBookingType, getDetailPageUrl} from './listing';
import {
    getDeviceSize,
    getIsMobile,
    getIsDesktop,
    getLocaleJS,
    getReviewPageSize,
    getLanguageNativeName,
    getLanguageName
} from './system';
import {labelMaker} from '../../server/utils/labelMaker';
import {isReviewsModalOpen} from './modalStates';
import get from 'lodash.get';
import {isInBucketData} from '../utils/abacusHelper';

const REVIEW_COUNT_MIN = 5;
const getJsReviews = createSelector(
    [getReviews],
    (reviewsData) => {
        return Iterable.isIterable(reviewsData) ? reviewsData.toJS() : reviewsData;
    }
);

const getBearStayBannerLabels = createSelector(
    [getTranslations],
    (trans) => {
        return {
            title: trans.bearStayNewTitle,
            description: trans.bearStayTeaserMessage,
            readReview: trans.bearStayTeaserButton
        };
    }
);

const getBearStayReview = createSelector(
    [getTranslations, getBearStayReviewData, getIsDesktop, getDeviceSize],
    (trans, bearStayReview, isDesktop, deviceSize) => {
        if (!bearStayReview) {
            return null;
        }

        let date = bearStayReview.stayedDate;
        const dateObj = new Date(date.split('T')[0].replace(/-/g, '/'));
        const splittedDate = dateObj.toDateString().split(' ');
        date = `${splittedDate[1]} ${splittedDate[2]}, ${splittedDate[3]}`;
        const content = bearStayReview.stayedComments;

        return {
            labels: {
                content,
                date,
                more: 'more',
                footerText: trans.bearStayTheBear,
                title: trans.bearStayReviewTitle,
                tooltipLabelText: trans.bearStayHelpLink
            },
            tooltipOptions: {
                delayShow: isDesktop,
                tooltipCentered: true,
                tooltipContent: trans.bearStayHelpContent,
                tooltipLabelText: trans.bearStayHelpLink,
                tooltipPlacement: isDesktop || deviceSize === 'md' ? 'left' : 'top',
                tooltipType: 'tooltip--lg'
            }
        };
    }
);

const getReviewsLabels = createSelector(
    [getJsReviews, getOwnerDetails, getTranslations, getLanguageNativeName],
    (reviews, owner, trans, languageNativeName) => {
        const viewAllReviews = reviews.reviewCount
            ? labelMaker(trans.viewAllReviews, reviews.reviewCount)
            : '';
        const seeAllReviews = reviews.reviewCount
            ? labelMaker(trans.seeAllReviews, reviews.reviewCount)
            : '';
        const postTranslationInfo = labelMaker(trans.postTranslationInfo, languageNativeName);

        const translateTo = labelMaker(trans.translateTo, languageNativeName);
        const labels = {
            nextPageOfReviews: trans.nextPageOfReviews,
            previousPageOfReviews: trans.previousPageOfReviews,
            reviews: labelMaker(trans.reviewCount, {
                REVIEWCOUNT: reviews.reviewCount
            }),
            ownershipTransferred: trans.ownershipTransferred,
            sortBy: trans.sortBy,
            sortByRecentMost: trans.sortByRecentMost,
            sortByRecentLeast: trans.sortByRecentLeast,
            filterBy: trans.filterBy,
            noFilter: trans.noFilter,
            filterByFiveStar: trans.filterByFiveStar,
            filterByFourStar: trans.filterByFourStar,
            filterByThreeStar: trans.filterByThreeStar,
            filterByTwoStar: trans.filterByTwoStar,
            filterByOneStar: trans.filterByOneStar,
            filterByVerifiedReview: trans.filterByVerifiedReview,
            filterByWithVerifiedReview: trans.filterByWithVerifiedReview,
            filterByWithoutVerifiedReview: trans.filterByWithoutVerifiedReview,
            averageRatingTitle: trans.averageRatingTitle,
            averageRating: labelMaker(trans.averageRatingText, {owner_first_name: owner.name}),
            totalReviews: trans.totalReviews,
            pagesize: trans.pagesize,
            latestReviews: trans.latestReviews,
            tenByPage: trans.tenByPage,
            twentyByPage: trans.twentyByPage,
            fiftyByPage: trans.fiftyByPage,
            of: trans.of,
            emptyReviews: trans.emptyReviews,
            by: trans.by,
            verifiedStay: trans.verifiedStay,
            submitted: trans.submitted,
            stayed: trans.stayed,
            source: trans.source,
            isHelpful: trans.isHelpful,
            yes: trans.yes,
            no: trans.no,
            more: trans.viewMore,
            less: trans.viewLess,
            readMore: trans.viewMore,
            ownersResponse: trans.ownersResponse,
            viewAllReviews,
            seeAllReviews,
            reportReviewActive: trans.reviewsReportLinkActive,
            reportReviewInactive: trans.reviewsReportLinkInactive,
            reviewsLegalDisclaimer: trans.reviewsLegalDisclaimer,
            skipToReviews: trans.skipToReviews,
            skipToReviewsAnchour: trans.skipToReviewsAnchour,
            translateTo,
            preTranslationInfo: trans.preTranslationInfo,
            postTranslationInfo,
            showOriginalReviews: trans.showOriginalReviews,
            translationErrorMsg: trans.translationErrorMsg
        };
        return labels;
    }
);

const getReviewList = createSelector(
    [getJsReviews],
    (reviewsData) => {
        return reviewsData.reviews;
    }
);

const getNumReviewsToShow = createSelector(
    [getIsMobile, getReviewPageSize, isReviewsModalOpen],
    (isMobile, reviewPageSize, reviewsModalOpen) => {
        const MOBILE_PREVIEW_COUNT = 3;

        if (isMobile && !reviewsModalOpen) {
            return MOBILE_PREVIEW_COUNT;
        }

        return reviewPageSize;
    }
);

const getPaginationInfo = createSelector(
    [getJsReviews, getNumReviewsToShow],
    (reviewsData, numReviewsToShow) => {
        const page = reviewsData.pageIndex;
        const pageSize = numReviewsToShow;
        const totalReviewCount = reviewsData.reviewCount;
        const pageStartIndex = (page - 1) * pageSize;
        const pageEndIndex =
            page * pageSize <= totalReviewCount ? page * pageSize : totalReviewCount;
        const reviewsStart = pageStartIndex === 0;
        const reviewsEnd = pageEndIndex === totalReviewCount;
        return {
            page,
            pageSize,
            totalReviewCount,
            pageStartIndex,
            pageEndIndex,
            reviewsStart,
            reviewsEnd
        };
    }
);

const getTranslationFeatureEnabled = createSelector(
    [getJsReviews, getPaginationInfo],
    (reviewsData, paginationInfo) => {
        const {pageStartIndex, pageEndIndex} = paginationInfo;
        const {reviews} = reviewsData;
        const isTranslationFeatureEnabled = reviews
            .slice(pageStartIndex, pageEndIndex)
            .some((review) => {
                return review.isNonSiteLanguageReview;
            });
        return isTranslationFeatureEnabled;
    }
);

const getFormattedReviews = createSelector(
    [getReviewList, getTravelerFeatures],
    (reviewList, travelerFeatures) => {
        const formattedReviews = reviewList.map((review) => {
            const {isReportReviewActive = true} = review;
            return {
                ...review,
                ...(travelerFeatures.hasReportReviewLink ? {isReportReviewActive} : {})
            };
        });
        return formattedReviews;
    }
);

const getFormattedTranslatedReviews = createSelector(
    [getJsReviews, getTravelerFeatures],
    (reviewsData, travelerFeatures) => {
        const {translatedReviewsMap, pageIndex} = reviewsData;
        const translatedReviewsList = get(translatedReviewsMap, pageIndex, []);
        const formattedTranslatedReviews = translatedReviewsList.map((review) => {
            const {isReportReviewActive = true} = review;

            return {
                ...review,
                ...(travelerFeatures.hasReportReviewLink ? {isReportReviewActive} : {})
            };
        });
        return formattedTranslatedReviews;
    }
);

const getFormattedTranslatedReviewsForMobile = createSelector(
    [
        getJsReviews,
        getFormattedReviews,
        getNumReviewsToShow,
        getLocaleJS,
        getTravelerFeatures,
        getIsMobile
    ],
    (reviewsData, formattedReviews, pageSize, locale, travelerFeatures, isMobile) => {
        let reviewsForMobile = [];
        if (isMobile) {
            const {translatedReviewsMap, pageIndex, reviewCount} = reviewsData;
            let pageNum;
            for (pageNum = 1; pageNum <= pageIndex; pageNum++) {
                const pageStartIndex = (pageNum - 1) * pageSize;
                const pageEndIndex =
                    pageNum * pageSize <= reviewCount ? pageNum * pageSize : reviewCount;
                const translatedReviewsList = get(translatedReviewsMap, pageNum, []);
                if (translatedReviewsList.length === 0) {
                    const formattedReviewsForPage = formattedReviews.slice(
                        pageStartIndex,
                        pageEndIndex
                    );
                    reviewsForMobile.push(...formattedReviewsForPage);
                } else {
                    const formattedTranslatedReviews = translatedReviewsList.map((review) => {
                        const {isReportReviewActive = true} = review;

                        return {
                            ...review,
                            ...(travelerFeatures.hasReportReviewLink ? {isReportReviewActive} : {})
                        };
                    });
                    reviewsForMobile.push(...formattedTranslatedReviews);
                }
            }
        }

        return reviewsForMobile;
    }
);

const getReviewSummaryData = createSelector(
    [getJsReviews],
    (reviewsData) => {
        // Translate the data received from the API to the contract
        // our components need
        const {
            averageRating,
            oneStarReviewCount,
            twoStarReviewCount,
            threeStarReviewCount,
            fourStarReviewCount,
            fiveStarReviewCount
        } = reviewsData;

        return {
            score: averageRating,
            totalOne: oneStarReviewCount,
            totalTwo: twoStarReviewCount,
            totalThree: threeStarReviewCount,
            totalFour: fourStarReviewCount,
            totalFive: fiveStarReviewCount
        };
    }
);

const getWriteReviewLinkData = createSelector(
    [getBookingType, getDetailPageUrl, getTranslations, getTravelerFeatures],
    (bookingType, detailPageUrl, trans, travelerFeatures) => {
        if (!bookingType.payPerBooking && travelerFeatures.hasWriteReviews) {
            const splitDetailPageUrl = detailPageUrl.split('/');
            const index = splitDetailPageUrl.length === 2 ? 1 : 2;
            splitDetailPageUrl.splice(index, 0, 'reviews/write');

            return {
                href: splitDetailPageUrl.join('/'),
                label: trans.writeReview
            };
        }

        return null;
    }
);

const getTranslatedReviewsMap = createSelector(
    [getJsReviews],
    (reviewsData) => {
        return reviewsData.translatedReviewsMap;
    }
);

const getFetchAutoTranslatedReviews = createSelector(
    [getJsReviews],
    (reviewsData) => {
        return reviewsData.fetchAutoTranslatedReviews;
    }
);

const getTranslateReviewsError = createSelector(
    [
        getJsReviews,
        getTranslationFeatureEnabled,
        getFetchAutoTranslatedReviews,
        getTranslatedReviewsMap
    ],
    (
        reviewsData,
        isTranslationFeatureEnabled,
        fetchAutoTranslatedReviews,
        translatedReviewsMap
    ) => {
        const {pageIndex} = reviewsData;
        const translatedReviewsList = get(translatedReviewsMap, pageIndex, null);

        return (
            isTranslationFeatureEnabled &&
            fetchAutoTranslatedReviews &&
            translatedReviewsList !== null &&
            translatedReviewsList.length === 0
        );
    }
);

const getTranslationButtonsVisibility = createSelector(
    [
        getJsReviews,
        getTranslationFeatureEnabled,
        getFetchAutoTranslatedReviews,
        getTranslateReviewsError
    ],
    (reviewsData, isTranslationFeatureEnabled, fetchAutoTranslatedReviews, hasTranslationError) => {
        const isTranslateFeatureVisible =
            isTranslationFeatureEnabled && (!fetchAutoTranslatedReviews || hasTranslationError);
        const isShowOriginalReviewsFeatureVisible =
            isTranslationFeatureEnabled && !isTranslateFeatureVisible;

        return {
            isTranslateFeatureVisible,
            isShowOriginalReviewsFeatureVisible
        };
    }
);

const getTranslationBoxSettings = createSelector(
    [getJsReviews, getReviewsLabels, getTranslationButtonsVisibility],
    (reviewsData, labels, translationButtonsVisibility) => {
        const {
            isTranslateFeatureVisible,
            isShowOriginalReviewsFeatureVisible
        } = translationButtonsVisibility;
        let translationButtonText = '';
        let translationButtonActionIsTranslate = isTranslateFeatureVisible;

        if (isTranslateFeatureVisible) {
            translationButtonText = labels.translateTo;
        }

        if (isShowOriginalReviewsFeatureVisible) {
            translationButtonText = labels.showOriginalReviews;
        }
        return {
            translationButtonText,
            translationButtonActionIsTranslate
        };
    }
);

const getOwnerRating = createSelector(
    [getJsReviews],
    (reviewsData) => {
        return reviewsData.ownerRating || {};
    }
);

/* Average Rating AB Testing */
const getMayShowAverageRating = createSelector(
    [getJsReviews],
    (reviewsData) => {
        const {reviewCount, ownerRating} = reviewsData;
        return !reviewCount && !!(ownerRating || {}).reviewCount;
    }
);

const getAbTestOwnerRating = createSelector(
    [getOwnerRating, getAbacusTests],
    (ownerRating, abacusTestData) => {
        const isBucketed = isInBucketData(abacusTestData, 'ha_pdp_avg_reviews_web_all_listings', 1);

        return isBucketed && ownerRating;
    }
);

const getReviewsSelector = createSelector(
    [
        getReviewsLabels,
        getMayShowAverageRating,
        getAbTestOwnerRating,
        getReviewSummaryData,
        getJsReviews,
        getFormattedReviews,
        getPaginationInfo,
        getNumReviewsToShow,
        getIsMobile,
        getBearStayReview,
        getTravelerFeatures,
        getWriteReviewLinkData,
        getTranslationFeatureEnabled,
        getTranslatedReviewsMap,
        getLanguageNativeName,
        getLanguageName,
        getFormattedTranslatedReviews,
        getFormattedTranslatedReviewsForMobile,
        getTranslationButtonsVisibility,
        getTranslateReviewsError,
        getTranslationBoxSettings
    ],
    (
        labels,
        mayShowAverageRating,
        ownerRating,
        reviewsSummaryData,
        reviewsData,
        formattedReviews,
        paginationInfo,
        numReviewsToShow,
        isMobile,
        bearStayReview,
        travelerFeatures,
        writeReviewLinkData,
        isTranslationFeatureEnabled,
        translatedReviewsMap,
        languageNativeName,
        languageName,
        formattedTranslatedReviews,
        formattedTranslatedReviewsForMobile,
        translationButtonsVisibility,
        hasTranslationError,
        translationBoxSettings
    ) => {
        const {
            page,
            pageSize,
            totalReviewCount,
            pageStartIndex,
            pageEndIndex,
            reviewsStart,
            reviewsEnd
        } = paginationInfo;

        const {isShowOriginalReviewsFeatureVisible} = translationButtonsVisibility;
        const reviewsListForSinglePage = isShowOriginalReviewsFeatureVisible
            ? formattedTranslatedReviews
            : formattedReviews.slice(pageStartIndex, pageEndIndex);
        const reviews = isShowOriginalReviewsFeatureVisible
            ? formattedTranslatedReviewsForMobile
            : formattedReviews;
        const reviewListForPreview = reviews.slice(0, numReviewsToShow);
        const reviewsRemainder = reviews.length % pageSize;
        const reviewListAllLoadedReviews =
            reviews.length > pageSize && reviewsRemainder !== 0
                ? reviews.slice(0, reviews.length - reviewsRemainder)
                : reviews;
        const hasMoreReviews = numReviewsToShow < totalReviewCount;
        const fetchedAllReviews = formattedReviews.length === totalReviewCount;
        const showReportLinks = travelerFeatures.hasReportReviewLink;
        const showReviewsLegalDisclaimer = travelerFeatures.showReviewsLegalDisclaimer;
        return {
            labels,
            ownerRating,
            mayShowAverageRating,
            reviewsSummaryData,
            page,
            hasMoreReviews,
            isFetching: reviewsData.isFetching,
            totalReviewCount,
            reviewsStart,
            reviewsEnd,
            reviewsListForSinglePage,
            reviewListForPreview,
            reviewListAllLoadedReviews,
            pageStartIndex: pageStartIndex + 1,
            pageEndIndex,
            isMobile,
            pageSize,
            fetchedAllReviews,
            reviewCountMin: REVIEW_COUNT_MIN,
            bearStayReview,
            showReportLinks,
            showReviewsLegalDisclaimer,
            writeReviewLinkData,
            isTranslationFeatureEnabled,
            languageNativeName,
            languageName,
            hasTranslationError,
            ...translationBoxSettings
        };
    }
);

export default getReviewsSelector;
export {
    getBearStayBannerLabels,
    getBearStayReview,
    getReviewsLabels,
    getReviewsSelector,
    getReviewList,
    getFormattedReviews,
    getReviewSummaryData,
    getNumReviewsToShow,
    getJsReviews,
    getWriteReviewLinkData
};
